import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

// Reusable structural style layout object matching enterprise form configurations
const enterpriseSearchInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT MATRIX ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const Search = ({ visible, meta, setMeta }) => {
    const deliveries = useSelector(state => state.deliveries);
    const [formValues, setFormValues] = useState(meta)
    const [searchButtonDisabled, setSearchButtonDisabled] = useState(true)
    const [checkFormUse, setCheckFormUse] = useState("")
    const [errors, setErrors] = useState({});
    
    const validator = (fieldValues) => {
        let temp = { ...errors }
        if(/\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:|\-|\s/g.test(fieldValues && fieldValues.order_id)){
            temp.order_id = "Please enter valid order number"
        }
        else{
            temp.order_id=""
        }
        if(/\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:|\-|\s/g.test(fieldValues && fieldValues.tracking_number)){
            temp.tracking_number = "Please enter valid tracking number"
        }else{
            temp.tracking_number=""
        }
        if(/\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:|\-|\s/g.test(fieldValues && fieldValues.receiver_province)){
                temp.receiver_province =  "Please enter valid receiver province"
        }else{
            temp.receiver_province = ""
        }
        if(/\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:|\-|\s/g.test(fieldValues && fieldValues.customer_name)){
                    temp.customer_name ="Please enter valid customer name"
        }else{
            temp.customer_name =""
        }
        setErrors({
            ...temp
        })

        if (fieldValues == formValues)
            return Object.values(temp).every(x => x == "")
    }
    const onInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value
        });
        validator({ [name]: value })
    };

    const checkFormHasValues = () => {
        let disableButton = true
        if (formValues["tracking_number"] != "" || formValues["customer_name"] != "" || formValues["order_id"] != "" || formValues["receiver_province"] != ""){
            disableButton = false
        }

        return disableButton
    }

    const handleKeyUp = (event) => {
        if (event.keyCode === 13) {
            const isValid = validator(formValues)
            if (isValid) {
                setMeta(prevState => ({
                    ...prevState,
                    ["order_id"]: formValues["order_id"],
                    ["tracking_number"]: formValues["tracking_number"],
                    ["customer_name"]: formValues["customer_name"],
                    [`receiver_province`]: formValues["receiver_province"],
                    [`page`]: 1,
                }));
                setCheckFormUse(true)
            }
        }
    }

    const submitSearchValues =e => {
        e.preventDefault()
        const isValid = validator(formValues)
        if (isValid) {
            setMeta(prevState => ({
                ...prevState,
                ["order_id"]: formValues["order_id"],
                ["tracking_number"]: formValues["tracking_number"],
                ["customer_name"]: formValues["customer_name"],
                [`receiver_province`]: formValues["receiver_province"],
                [`page`]: 1,
            }));
            setCheckFormUse(true)
        }
    }

    const resetFields = () => {
        setFormValues({
            ...formValues,
            ["order_id"]: "",
            ["tracking_number"]: "",
            ["customer_name"]: "",
            [`receiver_province`]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            ["order_id"]: "",
            ["tracking_number"]: "",
            ["customer_name"]: "",
            [`receiver_province`]: "",
            [`page`]: 1,
        }));
    }

    const onInputClear = (name) => {
        setErrors({ name: "" })
        setFormValues({
            ...formValues,
            [name]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            [name]: ""
        }));
    };

    useEffect(() => {
        setCheckFormUse("") 
        setSearchButtonDisabled(checkFormHasValues())
    }, [formValues])

    return (
        <>
            {visible &&
                <Box 
                    className="filtercontainer"
                    sx={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        p: 3,
                        mb: 3,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box 
                        className="filteraccordion" 
                        onKeyUp={handleKeyUp}
                        component="form"
                        noValidate
                        autoComplete="off"
                        onSubmit={e => { e.preventDefault(); }}
                    >
                        <Grid container spacing={2.5}>
                            
                            {/* 1. Order ID Input Identifier Parameter Field */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={formValues && formValues.order_id}
                                    name={"order_id"}
                                    label="Order Number"
                                    size={'small'}
                                    onChange={onInputChange}
                                    InputProps={{
                                        endAdornment: (formValues && formValues.order_id) ? (
                                            <IconButton size="small" onClick={() => onInputClear("order_id")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                    variant="outlined"
                                    error={!!errors.order_id}
                                    helperText={errors.order_id}
                                    sx={enterpriseSearchInputStyles}
                                />
                            </Grid>

                            {/* 2. Master Shipment Parcel Tracking Code Parameter Field */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={formValues && formValues.tracking_number}
                                    name={"tracking_number"}
                                    label="Tracking Number"
                                    size={'small'}
                                    onChange={onInputChange}
                                    InputProps={{
                                        endAdornment: (formValues && formValues.tracking_number) ? (
                                            <IconButton size="small" onClick={() => onInputClear("tracking_number")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                    variant="outlined"
                                    error={!!errors.tracking_number}
                                    helperText={errors.tracking_number}
                                    sx={enterpriseSearchInputStyles}
                                />
                            </Grid>

                            {/* 3. Destination Regional Boundary Province Code Parameter Field */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={formValues && formValues.receiver_province}
                                    name={"receiver_province"}
                                    label="Receiver Province Code"
                                    size={'small'}
                                    onChange={onInputChange}
                                    InputProps={{
                                        endAdornment: (formValues && formValues.receiver_province) ? (
                                            <IconButton size="small" onClick={() => onInputClear("receiver_province")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                    variant="outlined"
                                    error={!!errors.receiver_province}
                                    helperText={errors.receiver_province}
                                    sx={enterpriseSearchInputStyles}
                                />
                            </Grid>

                            {/* 4. Consignee B2C Customer Name Identifier Parameter Field */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={formValues && formValues.customer_name}
                                    name={"customer_name"}
                                    label="Customer Name"
                                    size={'small'}
                                    onChange={onInputChange}
                                    InputProps={{
                                        endAdornment: (formValues && formValues.customer_name) ? (
                                            <IconButton size="small" onClick={() => onInputClear("customer_name")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                    variant="outlined"
                                    error={!!errors.customer_name}
                                    helperText={errors.customer_name}
                                    sx={enterpriseSearchInputStyles}
                                />
                            </Grid>

                            {/* 5. Functional Submission Actions Footer Grid Anchor Layer */}
                            <Grid item xs={12}>
                                <Box 
                                    className="filterfooter"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: 1.5,
                                        pt: 1,
                                    }}
                                >
                                    <Button 
                                        variant="outlined" 
                                        onClick={resetFields} 
                                        disabled={searchButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 3,
                                            height: '38px',
                                            borderColor: '#E2E8F0',
                                            color: '#475569',
                                            '&:hover': {
                                                borderColor: '#CBD5E1',
                                                backgroundColor: '#F1F5F9',
                                                color: '#1E293B'
                                            },
                                            '&.Mui-disabled': {
                                                borderColor: '#E2E8F0',
                                                color: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={submitSearchValues} 
                                        disabled={searchButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 4,
                                            height: '38px',
                                            boxShadow: 'none',
                                            backgroundColor: '#1976d2',
                                            color: '#FFFFFF',
                                            '&:hover': { 
                                                backgroundColor: '#115293',
                                                boxShadow: 'none' 
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#E2E8F0',
                                                color: '#94A3B8'
                                            }
                                        }}
                                    >
                                        Search
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>
                </Box>
            }
        </>
    )
}

export default Search;